import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function SupplierOffers() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [productId, setProductId] = useState("");
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_id: "", supplier_name: "", offer_date: new Date().toISOString().slice(0, 10),
    valid_until: "", currency: "TRY", unit_price: 0, min_order: 1, delivery_days: 7,
    payment_terms: "", freight_included: false, customs_included: false,
    extras: { customs: 0, freight_intl: 0, freight_dom: 0, insurance: 0, bank: 0 },
  });

  useEffect(() => {
    (async () => {
      const [pr, su] = await Promise.all([api.get("/products"), api.get("/suppliers")]);
      setProducts(pr.data); setSuppliers(su.data);
      if (pr.data[0]) setProductId(pr.data[0].id);
    })();
  }, []);

  const loadOffers = async (pid) => {
    if (!pid) return;
    const { data } = await api.get(`/supplier-offers/compare/${pid}`);
    setOffers(data);
  };
  useEffect(() => { if (productId) loadOffers(productId); }, [productId]);

  const create = async () => {
    const prod = products.find(p => p.id === productId);
    const sup = suppliers.find(s => s.id === form.supplier_id);
    await api.post("/supplier-offers", {
      ...form,
      product_id: productId,
      product_code: prod?.code || "",
      product_name: prod?.name || "",
      supplier_name: sup?.name || form.supplier_name,
    });
    toast.success("Teklif eklendi");
    setOpen(false);
    loadOffers(productId);
  };

  return (
    <div className="space-y-6" data-testid="offers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Karşılaştırma</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mt-1">Tedarikçi Teklifleri</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger className="w-72" data-testid="offer-product-filter"><SelectValue placeholder="Ürün seçin" /></SelectTrigger>
            <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-offer-btn" className="bg-primary hover:bg-orange-600 text-white"><Plus size={16} className="mr-2" /> Teklif Ekle</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Yeni Tedarikçi Teklifi</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="offer-supplier"><SelectValue placeholder="Tedarikçi" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={form.offer_date} onChange={(e) => setForm({ ...form, offer_date: e.target.value })} />
                <Input type="date" placeholder="Geçerlilik" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                <Input type="number" placeholder="Birim Fiyat" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: Number(e.target.value) })} data-testid="offer-unit-price" />
                <Input type="number" placeholder="Min Sipariş" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: Number(e.target.value) })} />
                <Input type="number" placeholder="Teslim (gün)" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: Number(e.target.value) })} />
                <Input placeholder="Ödeme şekli" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
                <Input type="number" placeholder="Gümrük" value={form.extras.customs} onChange={(e) => setForm({ ...form, extras: { ...form.extras, customs: Number(e.target.value) } })} />
                <Input type="number" placeholder="Yurt Dışı Nakliye" value={form.extras.freight_intl} onChange={(e) => setForm({ ...form, extras: { ...form.extras, freight_intl: Number(e.target.value) } })} />
                <Input type="number" placeholder="Yurt İçi Nakliye" value={form.extras.freight_dom} onChange={(e) => setForm({ ...form, extras: { ...form.extras, freight_dom: Number(e.target.value) } })} />
                <Input type="number" placeholder="Sigorta" value={form.extras.insurance} onChange={(e) => setForm({ ...form, extras: { ...form.extras, insurance: Number(e.target.value) } })} />
              </div>
              <Button data-testid="save-offer" onClick={create} className="w-full mt-3 bg-primary hover:bg-orange-600 text-white">Kaydet</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-left"><tr>{["Sıra", "Tedarikçi", "Para", "Birim Fiyat", "Birim TL (Gerçek)", "Teslim (gün)", "Geçerlilik", "Skor", ""].map(h => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {offers.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Bu ürün için teklif yok.</td></tr>}
              {offers.map((o, i) => (
                <tr key={o.id} className={`border-t border-border ${i === 0 ? "bg-orange-50 dark:bg-orange-950/20" : "hover:bg-slate-50"}`}>
                  <td className="px-4 py-2 font-bold">{i + 1}</td>
                  <td className="px-4 py-2 font-semibold">{o.supplier_name} {i === 0 && <span className="ml-2 text-[10px] uppercase tracking-widest text-primary">En Avantajlı</span>}</td>
                  <td className="px-4 py-2">{o.currency}</td>
                  <td className="px-4 py-2 num">{fmt(o.unit_price)}</td>
                  <td className="px-4 py-2 num font-bold">{fmt(o.unit_total_try)}</td>
                  <td className="px-4 py-2">{o.delivery_days} gün</td>
                  <td className="px-4 py-2">
                    {o.expired ? (
                      <span className="inline-flex items-center gap-1 text-destructive text-xs"><AlertTriangle size={12} /> Süresi Dolmuş</span>
                    ) : (o.valid_until || "-")}
                  </td>
                  <td className="px-4 py-2 num font-bold text-primary">{o.score}</td>
                  <td className="px-4 py-2"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
